import {KpiRangeBreachSeverity} from '@hconnect/common/types'
import {GenericInput, Icon, TextMedium} from '@hm/fundamental'
import {mdiMessageOutline, mdiLayersOutline} from '@mdi/js'
import {IconButton, Tooltip} from '@mui/material'
import React, {useCallback} from 'react'
import {useController} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {KpiValueDto} from '../../types'
import {getLatestKpiMonthValue, getLatestKpiMonthValueOrDefault} from '../../utils'

type CommentsVersionsIconProps = {
  hasComments: boolean
}

const CommentsVersionsIcon = ({hasComments}: CommentsVersionsIconProps) => (
  <Icon path={hasComments ? mdiMessageOutline : mdiLayersOutline} customSize={0.7} />
)

type Props = {
  kpiValues: KpiValueDto[]
  month: number
  isEditable: boolean
  isCreatable: boolean
  onOpenCommentsVersions?: (kpiValueId: string) => void
}

type KpiValueProps = {
  kpiValue: KpiValueDto
  onOpenCommentsVersions?: (kpiValueId: string) => void
}

const getKpiColorClass = (kpiValueBreachSeverity: KpiValueDto['breachSeverity']) => {
  switch (kpiValueBreachSeverity) {
    case KpiRangeBreachSeverity.Alarm:
      return 'text-negative'
    case KpiRangeBreachSeverity.Warning:
      return 'text-warning'
  }
  return undefined
}

const EditableKpiValue = ({kpiValue, onOpenCommentsVersions}: KpiValueProps) => {
  const kpiValueId = kpiValue.id

  const {
    field: {value, onChange},
    fieldState: {error}
  } = useController({
    name: kpiValueId,
    defaultValue: kpiValue.value || null
  })

  const handleChange = useCallback(
    (inputValue: string) => {
      if (inputValue === '') {
        onChange(null)
        return
      }

      const numericValue = Number(inputValue)
      if (!isNaN(numericValue) && isFinite(numericValue)) {
        onChange(numericValue)
      }
    },
    [onChange]
  )

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }, [])

  const onIconClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      onOpenCommentsVersions?.(kpiValueId)
    },
    [kpiValueId, onOpenCommentsVersions]
  )

  const displayValue = value === null || value === undefined ? '' : value

  return (
    <GenericInput
      type="number"
      className={getKpiColorClass(kpiValue.breachSeverity)}
      value={displayValue}
      onChange={handleChange}
      onClick={handleClick}
      state={error ? 'error' : undefined}
      onIconClick={onIconClick}
      icon={kpiValue.hasComments ? mdiMessageOutline : mdiLayersOutline}
      hint={error?.message}
      data-cy="kpi-value-input"
    />
  )
}

const ReadOnlyKpiValue = ({kpiValue, onOpenCommentsVersions}: KpiValueProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {value, hasComments} = kpiValue
  const hasValueOrComments = (value !== undefined && value !== null) || hasComments

  return (
    <div className="flex items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>
      <TextMedium className={getKpiColorClass(kpiValue.breachSeverity)}>
        {kpiValue.value}
      </TextMedium>
      <Tooltip title={t('message.openVersionHistory')}>
        <IconButton
          sx={{
            visibility: hasValueOrComments ? 'visible' : 'hidden',
            mr: 1
          }}
          onClick={() => {
            onOpenCommentsVersions?.(kpiValue.id)
          }}
          data-test-id="version-history-button"
        >
          <CommentsVersionsIcon hasComments={kpiValue.hasComments} />
        </IconButton>
      </Tooltip>
    </div>
  )
}

export const SlaKpiTabsValuesTableCellMonthItem = ({
  kpiValues,
  month,
  isEditable,
  isCreatable,
  onOpenCommentsVersions
}: Props) => {
  const lastKpiValue = getLatestKpiMonthValue(kpiValues, month)

  if (!isEditable) {
    if (isCreatable && lastKpiValue && lastKpiValue.value === undefined) {
      return (
        <EditableKpiValue kpiValue={lastKpiValue} onOpenCommentsVersions={onOpenCommentsVersions} />
      )
    }

    return (
      <ReadOnlyKpiValue
        kpiValue={getLatestKpiMonthValueOrDefault(kpiValues, month)}
        onOpenCommentsVersions={onOpenCommentsVersions}
      />
    )
  }

  if (!lastKpiValue) {
    return null
  }

  return (
    <EditableKpiValue kpiValue={lastKpiValue} onOpenCommentsVersions={onOpenCommentsVersions} />
  )
}
